import { WORKSPACE_ROW_IDENTITY_KEYS, workspaceCompanyId } from "../../helper/companyEligibility";
import Defaults from "../../helper/defaults";
import {
  ALL_COLUMNS,
  isLeadContactColumnName,
  LEAD_CONTACT_COLUMNS,
} from "../Enrichment/EnrichmentGrid.helpers";
import { foundEmailFromWorkspaceRow } from "../Enrichment/foundEmail";

export const MAX_WORKSPACE_ENRICHMENT_TITLE_LENGTH = 200;

/** Presentable firmographic columns on workspace detail rows. */
export const WORKSPACE_STANDARD_COLUMNS = [
  "Name",
  "Description",
  "Industry",
  "City",
  "State",
  "Address",
  "Domain",
  "Emails",
  "Phone",
  "Revenue",
  "Headcount",
  "Size",
];

const WORKSPACE_STANDARD_COLUMN_SET = new Set(WORKSPACE_STANDARD_COLUMNS);

const WORKSPACE_ENRICHMENT_RESERVED_HEADERS = [
  "stagedId",
  ...WORKSPACE_STANDARD_COLUMNS,
  ...LEAD_CONTACT_COLUMNS.map((column) => column.name),
];
const WORKSPACE_RENDERED_GRID_HEADERS = ALL_COLUMNS.map((column) => column.name);

export function getWorkspaceEnrichmentReservedHeaders() {
  return new Set(WORKSPACE_ENRICHMENT_RESERVED_HEADERS);
}

export function normalizeWorkspaceEnrichmentTitle(title) {
  return String(title || "").trim().slice(0, MAX_WORKSPACE_ENRICHMENT_TITLE_LENGTH);
}

export function applyOptimisticWorkspaceEnrichmentLabel(columnLabels, columnKey, title) {
  if (!title) return columnLabels;
  return { ...(columnLabels || {}), [columnKey]: title };
}

export function getWorkspaceEnrichmentTitleCollision(
  title,
  enrichmentColumns = [],
  columnLabels = {}
) {
  const normalizedTitle = normalizeWorkspaceEnrichmentTitle(title);
  if (!normalizedTitle) return null;
  if (isLeadContactColumnName(normalizedTitle)) return normalizedTitle;
  const unavailableTitles = new Set([
    ...WORKSPACE_ENRICHMENT_RESERVED_HEADERS,
    ...WORKSPACE_RENDERED_GRID_HEADERS,
    ...(enrichmentColumns || []),
    ...Object.values(columnLabels || {}),
  ]);
  return unavailableTitles.has(normalizedTitle) ? normalizedTitle : null;
}

/**
 * Payload keys used to resolve workspace row identity, in precedence order.
 * Every identity key must be added here by hand so the resolver and rendered-column
 * exclusion stay aligned; this does not identify undeclared future internal keys.
 */
export { WORKSPACE_ROW_IDENTITY_KEYS } from "../../helper/companyEligibility";
const WORKSPACE_ROW_IDENTITY_KEY_SET = new Set(WORKSPACE_ROW_IDENTITY_KEYS);
/** Backend row metadata keys listed in `columns` that are never enrichment answers. */
const WORKSPACE_ROW_METADATA_KEY_SET = new Set(["_is_stale", "_isSyntheticId", "enrichment_origin"]);
export const resolveWorkspaceRowStagedId = workspaceCompanyId;

function isWorkspaceEnrichmentColumnKey(column) {
  return (
    typeof column === "string" &&
    column &&
    !WORKSPACE_STANDARD_COLUMN_SET.has(column) &&
    !WORKSPACE_ROW_IDENTITY_KEY_SET.has(column) &&
    !WORKSPACE_ROW_METADATA_KEY_SET.has(column)
  );
}

/**
 * Non-standard workspace column keys (AI / Enrich Workspace answers).
 */
export function extractWorkspaceEnrichmentColumnKeys(columns = []) {
  return (columns || []).filter(isWorkspaceEnrichmentColumnKey);
}

/**
 * Pull the original Search filter config from a workspace payload when present.
 * Create-workspace stores it as `source_query`; some responses may use `query`.
 */
export function extractWorkspaceSavedQuery(workspace) {
  if (!workspace || typeof workspace !== "object") return null;
  const candidate = workspace.source_query || workspace.query || workspace.spec;
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }
  return candidate;
}

/**
 * Normalize a workspace (list or detail) into the saved-search picker shape
 * used by the V4 banner selector and filter controller.
 */
export function mapWorkspaceToSavedSearchOption(workspace) {
  if (!workspace || typeof workspace !== "object") return null;
  const id = workspace.id || workspace._id || workspace.workspace_id;
  if (!id) return null;
  const query = extractWorkspaceSavedQuery(workspace);
  return {
    id: String(id),
    _id: String(id),
    name: workspace.name || "Untitled",
    query: query || undefined,
    created_at: workspace.created_at,
    company_count: workspace.company_count,
  };
}

export function filterStateFromSavedQuery(query) {
  const searchParams = query && typeof query === "object" ? query : {};
  return {
    termsInclude: searchParams.TermsInclude || Defaults.search.termsInclude,
    termsExclude: searchParams.TermsExclude || Defaults.search.termsExclude,
    industryInclude: searchParams.IndustryInclude || Defaults.search.industryInclude,
    industryExclude: searchParams.IndustryExclude || Defaults.search.industryExclude,
    locations: searchParams.Location || Defaults.search.locations,
    revenueLowerBound:
      searchParams.RevenueLowerBound ?? Defaults.search.revenueLowerBound,
    revenueUpperBound:
      searchParams.RevenueUpperBound ?? Defaults.search.revenueUpperBound,
    employeesLowerBound:
      searchParams.EmployeesLowerBound ?? Defaults.search.employeesLowerBound,
    employeesUpperBound:
      searchParams.EmployeesUpperBound ?? Defaults.search.employeesUpperBound,
    naturalQuery:
      searchParams.NaturalLanguageQuery || Defaults.search.naturalLanguageQuery,
  };
}

/**
 * Collect enrichment answer values keyed by column name for one workspace row.
 */
export function extractRowWorkspaceEnrichments(row = {}, enrichmentColumns = []) {
  if (!enrichmentColumns.length) return {};
  const enrichments = {};
  enrichmentColumns.forEach((col) => {
    if (WORKSPACE_ROW_IDENTITY_KEY_SET.has(col)) return;
    if (!Object.prototype.hasOwnProperty.call(row, col)) return;
    const value = row[col];
    if (value === null || value === undefined) return;
    enrichments[col] = value;
  });
  return enrichments;
}

/** @param {unknown} value @returns {number|null} A finite number; blank cells are null, not 0. */
function finiteOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** @param {unknown} value @returns {string|null} */
function nonBlankText(value) {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Rebuild a grid metric from a workspace row: the numeric pointEstimate cell plus the
 * hidden `_<kind>_bounds` band (BE #746). An older backend omits the band; the metric
 * then degrades to the pointEstimate alone.
 * @param {number|null} pointEstimate
 * @param {unknown} bounds
 */
function workspaceRowMetric(pointEstimate, bounds) {
  const band = bounds && typeof bounds === "object" ? /** @type {Record<string, unknown>} */ (bounds) : {};
  const metric = {
    pointEstimate,
    lowerBound: finiteOrNull(band.lowerBound),
    upperBound: finiteOrNull(band.upperBound),
  };
  // Description-derived headcount (ETL #97); the grid labels it "est." (FE #749).
  return band.estimated === true ? { ...metric, estimated: true } : metric;
}

/**
 * Map workspace grid rows into the EnrichmentGrid / search company shape.
 * Pass `enrichmentColumns` (from workspace.columns) to preserve Enrich Workspace answers.
 */
export function mapWorkspaceRowsToCompanies(rows = [], enrichmentColumns = []) {
  const usedKeys = new Set((rows || []).map(resolveWorkspaceRowStagedId).filter(Boolean));
  return (rows || []).map((row, index) => {
    let workspaceRowKey = `identity-less-workspace-row-${index}`;
    while (usedKeys.has(workspaceRowKey)) workspaceRowKey += "-";
    usedKeys.add(workspaceRowKey);
    const stagedId = resolveWorkspaceRowStagedId(row, index);

    const emailsRaw = row.Emails ?? row.emails ?? [];
    const emails = Array.isArray(emailsRaw)
      ? emailsRaw
      : String(emailsRaw || "")
          .split(",")
          .map((email) => email.trim())
          .filter(Boolean);

    const industry = row.Industry || row.industry || "";
    const headcount = finiteOrNull(row.Headcount ?? row.headcount);
    const revenue = finiteOrNull(row.Revenue ?? row.revenue);
    const address =
      row.Address ||
      [row.City, row.State].filter(Boolean).join(", ") ||
      "";

    return {
      stagedId,
      workspaceRowKey: stagedId || workspaceRowKey,
      _is_stale: Boolean(row._is_stale),
      _isSyntheticId: Boolean(row._isSyntheticId),
      primaryName: row.Name || row.primaryName || "N/A",
      primaryLogo: row.primaryLogo || null,
      descriptive: row.Description || row.description ? [row.Description || row.description] : [],
      storedIndustry: industry,
      primaryLocation: { fullAddress: address },
      primaryWebsite: row.Domain || row.primaryWebsite || "",
      emails,
      // FE #750: the newest found Find-and-Verify email, merged into the Emails cell.
      foundEmail: foundEmailFromWorkspaceRow(row),
      ...(Array.isArray(row._lead_contacts) ? {leadContacts: row._lead_contacts} : {}),
      primaryTelephone: row.Phone || row.primaryTelephone || "",
      harmonizedHeadcount: workspaceRowMetric(headcount, row._headcount_bounds),
      revenue: workspaceRowMetric(revenue, row._revenue_bounds),
      // Backend display text (same as the CSV export); the grid prefers it when present.
      headcountDisplay: nonBlankText(row._headcount_display),
      revenueDisplay: nonBlankText(row._revenue_display),
      workspaceEnrichments: extractRowWorkspaceEnrichments(row, enrichmentColumns),
    };
  });
}

/**
 * Attach stored workspace enrichment answers onto live search companies by stagedId.
 */
export function mergeWorkspaceEnrichmentsOntoCompanies(
  companies = [],
  enrichmentByCompanyId = {},
  enrichmentColumns = []
) {
  if (!companies.length) return companies;
  if (!enrichmentColumns.length && !Object.keys(enrichmentByCompanyId || {}).length) {
    return companies;
  }

  return companies.map((company) => {
    const stagedId = String(company?.stagedId || "");
    const {_lead_contacts: leadContacts, ...fromWorkspace} = enrichmentByCompanyId?.[stagedId] || {};
    const existing = company.workspaceEnrichments || {};
    const merged = { ...fromWorkspace, ...existing };
    if (enrichmentColumns.length) {
      enrichmentColumns.forEach((col) => {
        if (merged[col] === undefined && fromWorkspace[col] !== undefined) {
          merged[col] = fromWorkspace[col];
        }
      });
    }
    if (!Object.keys(merged).length && company.workspaceEnrichments && !leadContacts) {
      return company;
    }
    return {
      ...company,
      ...(Array.isArray(leadContacts) ? {leadContacts} : {}),
      workspaceEnrichments: merged,
    };
  });
}

/**
 * Build EnrichmentGrid statusMap entries from company.workspaceEnrichments.
 */
export function buildWorkspaceEnrichmentStatusMap(companies = [], enrichmentColumns = []) {
  const statusMap = {};
  (enrichmentColumns || [])
    .filter((col) => !WORKSPACE_ROW_IDENTITY_KEY_SET.has(col))
    .forEach((col) => {
      statusMap[col] = {};
    });

  (companies || []).forEach((company) => {
    const stagedId = company?.stagedId || company?.workspaceRowKey;
    if (!stagedId) return;
    const enrichments = company.workspaceEnrichments || {};
    const columns = (
      enrichmentColumns.length > 0 ? enrichmentColumns : Object.keys(enrichments)
    ).filter((col) => !WORKSPACE_ROW_IDENTITY_KEY_SET.has(col));

    columns.forEach((col) => {
      if (!statusMap[col]) statusMap[col] = {};
      const raw = enrichments[col];
      if (raw === null || raw === undefined || raw === "") {
        // Workspace detail serializes both untouched and unusable answers as blank.
        // With no known outcome, omit the entry so the grid renders an inactive em dash.
        return;
      }
      statusMap[col][stagedId] = { status: "success", result: String(raw) };
    });
  });

  return statusMap;
}

/**
 * Merge persisted workspace answers with a live in-flight enrichment status map.
 * While a column is actively processing (present in `liveStatusMap`), live cell
 * entries win so non-targeted rows stay blank (—) instead of premature "No answer".
 */
export function mergeWorkspaceEnrichmentStatusMaps(baseStatusMap = {}, liveStatusMap = {}) {
  const columnKeys = new Set([
    ...Object.keys(baseStatusMap || {}),
    ...Object.keys(liveStatusMap || {}),
  ]);
  const merged = {};

  columnKeys.forEach((col) => {
    merged[col] = {
      ...(baseStatusMap?.[col] || {}),
      ...(liveStatusMap?.[col] || {}),
    };
  });

  return merged;
}

/**
 * Keep the latest run summary scoped to its submitted rows while live entries exist.
 * Cell rendering uses the merged map above so persisted answers outside that run remain visible.
 */
export function buildWorkspaceEnrichmentSummaryStatusMap(
  baseStatusMap = {},
  liveStatusMap = {}
) {
  const summary = {};
  const columnKeys = new Set([
    ...Object.keys(baseStatusMap || {}),
    ...Object.keys(liveStatusMap || {}),
  ]);

  columnKeys.forEach((col) => {
    const live = liveStatusMap?.[col];
    summary[col] = {
      ...(live && typeof live === "object" ? live : baseStatusMap?.[col] || {}),
    };
  });

  return summary;
}

/**
 * Seed a generated-column status map with loading cells for the companies being enriched.
 */
export function buildRunningEnrichmentStatusForCompanies(companyIds = []) {
  const entries = {};
  (companyIds || []).forEach((id) => {
    const stagedId = String(id || "").trim();
    if (!stagedId) return;
    entries[stagedId] = { status: "running" };
  });
  return entries;
}

/**
 * Index enrichment answers by company stagedId from a workspace detail payload.
 */
export function indexWorkspaceEnrichmentsByCompanyId(rows = [], enrichmentColumns = []) {
  const byId = {};
  (rows || []).forEach((row, index) => {
    const stagedId = resolveWorkspaceRowStagedId(row, index);
    if (!stagedId) return;
    byId[stagedId] = extractRowWorkspaceEnrichments(row, enrichmentColumns);
    if (Array.isArray(row._lead_contacts)) byId[stagedId]._lead_contacts = row._lead_contacts;
  });
  return byId;
}
